import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import { Edge, Node, useEdgesState, useNodesState } from 'reactflow'

import { FeedbackAlertContext } from '../../components/FeedbackAlert'
import { useOriginItemTree } from '../../components/TraceabilityTree/hooks/useOriginItemTree'
import { getLayoutedElements } from '../../components/TraceabilityTree/utils/getLayoutedElements'
import { AuthContext } from '../../contexts/auth'
import { CompanySegment } from '../../sdk/common/company.enum'
import {
  ItemResponseDTO,
  ItemTraceabilityCreateDTO,
} from '../../sdk/items/items.types'
import { sdk } from '../../services/sdk'

import { OriginItem } from './origin-items.types'
import { getItemDataRecursive } from './origin-items.utils'

type Tab = 'initial' | 'edit' | 'sheet'

interface OriginItemsDataContext {
  tab: Tab
  initialNodes: Node<any>[]
  initialEdges: Edge<any>[]
  nodeTypes: any
  nodes: Node<any, string | undefined>[]
  edges: Edge<any>[]
  direction: 'LR' | 'TB'
  currentNode: OriginItem | null
  originItem?: ItemResponseDTO
  editMode: string
  submitError: string
  submitItemError: string
  allowMultipleLevels: boolean
  loading: boolean

  actions: {
    setTab: (tab: Tab) => void
    loadLayout: () => void
    setDirection: (value: 'LR' | 'TB') => void
    handleNodeClick: (value: OriginItem, edit?: boolean) => void
    saveNewNodes: (value: OriginItem) => void
    setEditMode: (value: string) => void
    deleteNode: (id: string) => void
    handleSubmit: () => void
    handleSubmitSheet: (sheet: File) => Promise<void>
    setNodeWidth: (width: number) => void
    setNodeHeight: (height: number) => void
  }
}

interface OriginItemsProviderProps {
  children: React.ReactNode
}

export const OriginItemsContext = createContext({} as OriginItemsDataContext)

export function OriginItemsProvider(props: OriginItemsProviderProps) {
  const [itemData, setItemData] = useState<OriginItem | null>(null)
  const [originItem, setOriginItem] = useState<ItemResponseDTO>()
  const [editMode, setEditMode] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const [tab, setTab] = useState<Tab>('initial')

  const [submitItemError, setItemSubmitError] = useState('')
  const [submitError, setSubmitError] = useState('')

  const [direction, setDirection] = useState<'LR' | 'TB'>('TB')

  // Only allow multiple levels for verticalized companies
  const [allowMultipleLevels, setAllowMultipleLevels] = useState(false)

  const { initialNodes, initialEdges, nodeTypes } = useOriginItemTree({
    originItem: itemData || ({} as OriginItem),
  })

  const [nodes, setNodes] = useNodesState(initialNodes)
  const [edges, setEdges] = useEdgesState(initialEdges)

  const [nodeWidth, setNodeWidth] = useState(0)
  const [nodeHeight, setNodeHeight] = useState(0)

  const { user } = useContext(AuthContext)

  const params = useParams()

  useEffect(() => {
    if (
      user?.company?.verticalized ||
      user?.company?.segment === CompanySegment.FASHION_COMPANY
    ) {
      setAllowMultipleLevels(true)
    }
  }, [user?.company])

  const fetchInvoice = async () => {
    try {
      const response = await sdk.traceability.recursiveItem(params.id as string)
      if (response) {
        setOriginItem(response)

        setItemData(getItemDataRecursive(response))
      }
    } catch (error) {}
  }

  useEffect(() => {
    fetchInvoice()
  }, [])

  const loadLayout = useCallback(() => {
    if (!itemData) return

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      initialNodes,
      initialEdges,
      direction,
      nodeWidth,
      nodeHeight,
    )

    setNodes(JSON.parse(JSON.stringify(layoutedNodes)))
    setEdges(JSON.parse(JSON.stringify(layoutedEdges)))
  }, [itemData, setNodes, setEdges, direction, initialNodes, initialEdges])

  useEffect(() => {
    loadLayout()

    setSubmitError('')
  }, [loadLayout, initialNodes, initialEdges])

  const [currentNode, setCurrentNode] = useState<OriginItem | null>(null)

  const handleNodeClick = (data: OriginItem, edit?: boolean) => {
    setEditMode(edit ? data.code : '')
    setTab('edit')

    setCurrentNode(data)
  }

  const validate = (data: OriginItem) => {
    if (
      !data?.itemProviderRelation ||
      !data?.itemProviderRelation.companyProviderTaxId ||
      !data?.itemProviderRelation?.companyClientTaxId
    ) {
      setItemSubmitError('É obrigatório cadastrar dados do fornecedor')
      return false
    }

    if (!data?.code) {
      setItemSubmitError('É obrigatório cadastrar o código do item')
      return false
    }

    setItemSubmitError('')
    return true
  }

  const editNode = (
    nodeId: string,
    itemTree: OriginItem,
    newData: OriginItem,
  ) => {
    if (itemTree.nodeId === nodeId) {
      return newData
    }

    const originItems: OriginItem[] =
      itemTree.originItems?.map((oi) => editNode(nodeId, oi, newData)) ?? []

    return { ...itemTree, originItems }
  }

  const addNode = (
    parentId: string,
    itemTree: OriginItem,
    newData: OriginItem,
  ): OriginItem => {
    if (itemTree.nodeId === parentId) {
      newData = {
        ...newData,
        level: (itemTree.level ?? 0) + 1,
        originItems: [],
      }

      itemTree.originItems = [...(itemTree.originItems ?? []), newData]
    }

    const originItems: OriginItem[] =
      itemTree.originItems?.map((oi) => addNode(parentId, oi, newData)) ?? []

    return { ...itemTree, originItems }
  }

  const recursivelyDeleteNode = (id: string, itemTree: OriginItem) => {
    if (itemTree.nodeId === id) {
      return null
    }

    let originItems: OriginItem[] = []

    if (itemTree.originItems) {
      originItems = itemTree.originItems
        ?.map((oi) => recursivelyDeleteNode(id, oi))
        ?.filter((oi) => !!oi) as OriginItem[]
    }

    return { ...itemTree, originItems }
  }

  const deleteNode = (id: string) => {
    if (!itemData) return

    setItemData(recursivelyDeleteNode(id, itemData))

    setTab('initial')
  }

  const saveNewNodes = (data: OriginItem) => {
    if (!currentNode) return

    if (!validate(data)) return

    let _itemData

    // Is editting current node data
    if (editMode === currentNode?.code) {
      _itemData = editNode(currentNode?.nodeId ?? '', itemData!, data)
    } else {
      // Add node to parent
      _itemData = addNode(currentNode?.nodeId ?? '', itemData!, data)
    }

    setItemData(_itemData)

    setTab('initial')
  }

  const parseDataToTree = (data: OriginItem) => {
    const item: ItemTraceabilityCreateDTO = {
      id: data.id || undefined,
      code: data.code || undefined,
      description: data.description || undefined,
      quantity: data.quantity ? parseFloat(String(data.quantity)) : undefined,
      taxUnit: data.taxUnit || undefined,
      evidence: data.evidence?.files?.length ? data.evidence : undefined,
      itemProviderRelation: {
        companyClientTaxId: data.itemProviderRelation?.companyClientTaxId ?? '',
        companyProviderEmail:
          data.itemProviderRelation?.companyProviderEmail ?? '',
        companyProviderTaxId:
          data.itemProviderRelation?.companyProviderTaxId ?? '',
      },
      referenceId: data.referenceId ?? '',
    }

    if (
      data.metadata?.transportationData?.length ||
      data.metadata?.cattleData?.length ||
      data.metadata?.labelData?.length ||
      data.metadata?.certificationData?.length
    ) {
      item.metadata = data.metadata
    }

    if (
      data.itemInvoice?.invoiceCode ||
      data.itemInvoice?.invoiceCreateDate ||
      data.itemInvoice?.invoiceFile ||
      data.itemInvoice?.invoiceKey
    ) {
      item.itemInvoice = {
        invoiceCode: data.itemInvoice?.invoiceCode ?? '',
        invoiceCreateDate: data.itemInvoice?.invoiceCreateDate
          ? new Date(data.itemInvoice?.invoiceCreateDate)
          : undefined,
        file: data.itemInvoice?.invoiceFile,
        invoiceAccessKey: data.itemInvoice?.invoiceKey,
      }
    }

    item.originItems = data?.originItems?.length
      ? data?.originItems?.map((oi) => parseDataToTree(oi))
      : undefined

    return item
  }

  const { feedbackAlert } = useContext(FeedbackAlertContext)
  const navigate = useNavigate()

  const handleSubmit = async () => {
    setSubmitError('')
    setLoading(true)
    try {
      if (!itemData?.originItems?.length) {
        setSubmitError(
          'É obrigatório cadastrar pelo menos uma origem para o item',
        )
        return
      }

      const tree: ItemTraceabilityCreateDTO[] = itemData.originItems.map((oi) =>
        parseDataToTree(oi),
      )

      await sdk.items.createItemTraceability(itemData.id, tree)

      navigate(-1)
      feedbackAlert(
        'Origem do item cadastrado com sucesso e os dados já estão sendo processados. Em breve será possível acessar a rastreabilidade completa do item.',
        'success',
      )
    } catch (error) {
      setSubmitError('Ocorreu um erro ao cadastrar a origem do item.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitSheet = async (sheet: File) => {
    await sdk.items.uploadSheet(params.id ?? '', sheet)

    navigate(-1)
    feedbackAlert(
      'Origem do item cadastrado com sucesso e os dados já estão sendo processados. Em breve será possível acessar a rastreabilidade completa do item.',
      'success',
    )
  }

  console.log(
    itemData?.id,
    itemData?.originItems?.map((oi) => parseDataToTree(oi)),
  )

  return (
    <OriginItemsContext.Provider
      value={{
        tab,
        initialNodes,
        initialEdges,
        nodeTypes,
        nodes,
        edges,
        direction,
        currentNode,
        originItem,
        editMode,
        submitItemError,
        submitError,
        allowMultipleLevels,
        loading,
        actions: {
          setTab,
          loadLayout,
          setDirection,
          handleNodeClick,
          saveNewNodes,
          deleteNode,
          setEditMode,
          handleSubmit,
          handleSubmitSheet,
          setNodeWidth,
          setNodeHeight,
        },
      }}
    >
      {props.children}
    </OriginItemsContext.Provider>
  )
}
